// Het voorstel op de kaart 'Deelnemers om te koppelen': welke bestaande
// deelnemers lijken op de naam die iemand bij de naamstap invulde?
//
// Nodig omdat er in de app nog geen naamvergelijking bestaat; geen pakket,
// geen AI — de namen blijven in de browser van de medewerker, die ze al mocht
// zien (besluit 4). De helper rangschikt alleen: koppelen doet altijd een
// medewerker (besluit 6), ook bij een sterk voorstel.
//
// Vergelijkt met `Personas.Name` (één veld, "Voornaam Achternaam"); de losse
// PersonaData-regels lezen kost een read per deelnemer en staat buiten v1.
//
// AI-CONTEXT: See docs/features/deelnemer-koppeling-algemene-link.md

/**
 * Tussenvoegsels en lidwoorden die bij het vergelijken van de achternaam niet
 * meetellen: 'Berg' en 'van den Berg' zijn hier dezelfde achternaam.
 */
const PARTICLES = new Set([
    'van', 'von', 'de', 'der', 'den', 'het', 't', 'ten', 'ter', 'te', 'in', 'op', 'aan',
    'la', 'le', 'du', 'da', 'di', 'del', 'el', 'al',
])

// Drempels. Overeenkomst = 1 - afstand / langste naam; 1 is gelijk.
const STRONG_PART = 0.8 // voornaam én achternaam minstens zo dichtbij → sterk
const MIN_SURNAME = 0.75 // de achternaam moet altijd dichtbij zijn
const MIN_SCORE = 0.7 // anders geen voorstel
const INITIAL_MATCH = 0.6 // 'J.' tegen 'Jan': past, maar zegt weinig
const SWAPPED_FACTOR = 0.9 // voor- en achternaam omgedraaid ingevuld
const SURNAME_WEIGHT = 0.6

/**
 * @typedef {object} PersonaCandidate
 * @property {string} personaId - Personas.ID
 * @property {string} name - Personas.Name, zoals hij op de kaart staat
 * @property {string} status - Personas.Status ('' als die ontbreekt)
 * @property {number} score - 0..1, hoger is dichterbij
 * @property {'strong'|'weak'} strength - Sterk: voor- en achternaam lijken allebei. Zwak: een voorletter, omgedraaid, of een minder zekere voornaam
 * @property {boolean} exact - Dezelfde naam als ingevuld, op hoofdletters, accenten en spaties na (tussenvoegsels tellen wél)
 */

/**
 * Kleine letters, accenten weg, alles wat geen letter of cijfer is wordt een
 * spatie, spaties samengevoegd.
 * @param {string} [value]
 * @returns {string}
 */
export const normalizeName = (value) => String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

/**
 * Damerau-Levenshtein (optimal string alignment): invoegen, weglaten,
 * vervangen en twee verwisselde buurletters kosten elk één.
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
export const nameDistance = (a, b) => {
    const rows = a.length + 1
    const cols = b.length + 1
    /** @type {number[][]} */
    const d = Array.from({ length: rows }, (_, i) => Array.from({ length: cols }, (__, j) => (i === 0 ? j : j === 0 ? i : 0)))
    for (let i = 1; i < rows; i++) {
        for (let j = 1; j < cols; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1
            d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost)
            if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
                d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1)
            }
        }
    }
    return d[rows - 1][cols - 1]
}

/** @param {string} a @param {string} b @returns {number} 0..1 */
const similarity = (a, b) => (a && b ? 1 - nameDistance(a, b) / Math.max(a.length, b.length) : 0)

/** @param {string} [value] @returns {string[]} De woorden zonder tussenvoegsels */
const nameWords = (value) => normalizeName(value).split(' ').filter((t) => t && !PARTICLES.has(t))

/**
 * Een opgeslagen naam ("Voornaam Achternaam", één veld) gesplitst naar de vorm
 * van wat er is ingevuld: de achternaam is zijn LAATSTE woorden, evenveel als de
 * ingevulde achternaam er heeft. Niet "alles na het eerste woord": dan valt elke
 * deelnemer met twee voornamen buiten de vergelijking (testronde 2026-10-13, B9).
 *
 * @param {string[]} words - nameWords van de opgeslagen naam
 * @param {number} surnameCount - Aantal woorden in de ingevulde achternaam (≥ 1)
 * @returns {{first: string, surname: string}}
 */
const splitStoredName = (words, surnameCount) => (words.length <= surnameCount
    ? { first: '', surname: words.join('') }
    : { first: words[0], surname: words.slice(-surnameCount).join('') })

/**
 * Voornaam tegen voornaam. Is een van beide een voorletter, dan telt alleen of
 * hij past — en is het voorstel hooguit zwak.
 * @param {string} a
 * @param {string} b
 * @returns {{sim: number, initial: boolean}}
 */
const firstNameSimilarity = (a, b) => {
    if (!a || !b) return { sim: 0, initial: false }
    if (a.length === 1 || b.length === 1) {
        return { sim: (a.length === 1 ? b.startsWith(a) : a.startsWith(b)) ? INITIAL_MATCH : 0, initial: true }
    }
    return { sim: similarity(a, b), initial: false }
}

/**
 * @param {{first: string, surname: string}} input
 * @param {{first: string, surname: string}} candidate
 * @returns {{score: number, strong: boolean}|null}
 */
const compare = (input, candidate) => {
    const surSim = similarity(input.surname, candidate.surname)
    if (surSim < MIN_SURNAME) return null
    const { sim: firstSim, initial } = firstNameSimilarity(input.first, candidate.first)
    const score = SURNAME_WEIGHT * surSim + (1 - SURNAME_WEIGHT) * firstSim
    if (score < MIN_SCORE) return null
    return { score, strong: !initial && surSim >= STRONG_PART && firstSim >= STRONG_PART }
}

/**
 * @param {string} firstName - Zoals ingevuld bij de naamstap
 * @param {string} lastName - Zoals ingevuld bij de naamstap
 * @param {Array<{ID: string, Name?: string, Status?: string, UserID?: string, Deleted?: boolean}>|null|undefined} personas
 * @returns {PersonaCandidate[]} Exacte naam eerst, dan sterkst; gelijke score op naam en dan ID
 */
const rankPersonaCandidates = (firstName, lastName, personas) => {
    const firstWords = nameWords(firstName)
    const lastWords = nameWords(lastName)
    if (lastWords.length === 0 || !Array.isArray(personas)) return []
    const input = { first: firstWords[0] || '', surname: lastWords.join('') }
    const typed = normalizeName(`${firstName ?? ''} ${lastName ?? ''}`)
    // Omgedraaid ingevuld: de ingevulde voornaam is dan de achternaam.
    const swappedInput = { first: lastWords[0], surname: firstWords.join('') }

    return personas
        // Medewerkers (een Persona met UserID) en verwijderde deelnemers nooit.
        .filter((p) => p && p.Deleted !== true && !p.UserID)
        .map((p) => {
            const words = nameWords(p.Name)
            const direct = compare(input, splitStoredName(words, lastWords.length))
            const swapped = firstWords.length > 0
                ? compare(swappedInput, splitStoredName(words, firstWords.length))
                : null
            const best = direct || (swapped && { score: swapped.score * SWAPPED_FACTOR, strong: false })
            if (!best) return null
            return {
                personaId: p.ID,
                name: p.Name || '',
                status: p.Status || '',
                score: best.score,
                strength: best.strong ? 'strong' : 'weak',
                exact: normalizeName(p.Name) === typed,
            }
        })
        .filter(Boolean)
        // Een exacte naam altijd bovenaan, ook bij een gelijke score: hij staat ook vooraf gekozen.
        .sort((a, b) => Number(b.exact) - Number(a.exact) || b.score - a.score || a.name.localeCompare(b.name) || String(a.personaId).localeCompare(String(b.personaId)))
}

/**
 * Welke deelnemer staat op de kaart al gekozen:
 * - precies één exacte naam → die, ook naast een lijkende ('Jan de Vries' naast
 *   'Jan Fries'; besluit Gijs 2026-10-14);
 * - twee of meer exacte namen → niets: dat is echte twijfel;
 * - geen exacte naam → alleen een eenduidig sterk voorstel.
 * Anders niets; dan kiest de medewerker zelf uit 'Voorstel', waar de beste
 * bovenaan staat. Koppelen blijft altijd zijn klik (besluit 6).
 *
 * @param {PersonaCandidate[]} candidates - Uitkomst van rankPersonaCandidates
 * @returns {string} Personas.ID, of ''
 */
export const suggestedPersonaId = (candidates) => {
    const list = candidates || []
    const exact = list.filter((c) => c.exact)
    if (exact.length > 0) return exact.length === 1 ? exact[0].personaId : ''
    const strong = list.filter((c) => c.strength === 'strong')
    return strong.length === 1 ? strong[0].personaId : ''
}

export default rankPersonaCandidates
